import { useEffect, useRef, useState } from "react";

/**
 * Subscribe to live intervention-status pushes for a single alert.
 *
 * The hook opens a WebSocket against the same backend host that serves the
 * REST API, listens for `snapshot` + `intervention.update` payloads, and
 * exposes:
 *   - `live`      → true while the socket is OPEN (UI shows a "LIVE" pill)
 *   - `lastEvent` → the last message received, useful for one-shot effects
 *
 * The caller passes `onIntervention(intervention)` so it can merge the new
 * intervention object into its own alert state. Auto-reconnects with
 * exponential backoff (capped at 15 s) up to `maxRetries` attempts.
 */
export function useAlertStatusStream(alertId, { onIntervention } = {}) {
  const [live, setLive] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const timerRef = useRef(null);
  const closedRef = useRef(false);
  const cbRef = useRef(onIntervention);

  // Always invoke the latest callback without re-opening the socket.
  useEffect(() => { cbRef.current = onIntervention; }, [onIntervention]);

  useEffect(() => {
    if (!alertId) return undefined;
    closedRef.current = false;

    const buildWsUrl = () => {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const wsBase = base.replace(/^http/i, "ws");
      return `${wsBase}/api/ws/alerts/${encodeURIComponent(alertId)}`;
    };

    const open = () => {
      if (closedRef.current) return;
      const ws = new WebSocket(buildWsUrl());
      wsRef.current = ws;
      ws.onopen = () => {
        setLive(true);
        retryRef.current = 0;
      };
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          setLastEvent(msg);
          if (msg && (msg.type === "snapshot" || msg.type === "intervention.update")) {
            cbRef.current?.(msg.intervention, msg);
          }
        } catch (err) {
          console.error("ws message parse failed", err);
        }
      };
      ws.onclose = (ev) => {
        setLive(false);
        wsRef.current = null;
        // 4404 = backend rejected (alert not found) → don't retry forever.
        if (closedRef.current || ev?.code === 4404) return;
        const attempt = Math.min(retryRef.current + 1, 5);
        retryRef.current = attempt;
        const delay = Math.min(15000, 1000 * 2 ** (attempt - 1));
        timerRef.current = setTimeout(open, delay);
      };
      ws.onerror = () => {
        // onclose runs immediately after; let it handle the retry.
      };
    };

    open();
    return () => {
      closedRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        try { ws.close(1000, "unmount"); } catch (e) { console.error("ws close failed", e); }
      }
      wsRef.current = null;
    };
  }, [alertId]);

  return { live, lastEvent };
}
